import { useState } from "react";
import { RadioGroup } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/solid";
import { SparklesIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";

import {
  SubscriptionPlan,
  BillingFrequency,
  PaymentMethod,
  useStartCheckout,
  useChangeSubscription,
} from "hooks/useStripe";
import { InvoiceCheckoutModal } from "components/Billing/InvoiceCheckoutModal";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const frequencies: {
  value: BillingFrequency;
  label: string;
  priceSuffix: string;
}[] = [
  { value: BillingFrequency.Month, label: "Monatlich", priceSuffix: "/Monat" },
  { value: BillingFrequency.Year, label: "Jährlich", priceSuffix: "/Jahr" },
];

const paymentMethods: { value: PaymentMethod; label: string }[] = [
  { value: PaymentMethod.Card, label: "settings.billing.pm_card" },
  { value: PaymentMethod.Sepa, label: "settings.billing.pm_sepa" },
  { value: PaymentMethod.Invoice, label: "settings.billing.pm_invoice" },
];

const tiers = [
  {
    name: "Einzelkämpfer",
    id: SubscriptionPlan.Free,
    price: { month: "€159", year: "€1.908" },
    description: "Die Basis-Version für Einsteiger.",
    features: [
      "1 User",
      "30 Tage Testversion",
      "Monatlich kündbar",
      "Täglich aktualisierte Normen",
      "Richtlinienrecherche",
      "10 Jahre Daten-Speicherung",
    ],
    mostPopular: false,
    continuous: false,
  },
  {
    name: "Dienstleister",
    id: SubscriptionPlan.ProMd,
    price: { month: "€318", year: "€3.816" },
    description: "Der beste Preis für Ingenieurbüros.",
    features: [
      "2 User",
      "30 Tage Testversion",
      "Monatlich kündbar",
      "Unbegrenzte Projekte",
      "10 Jahre Daten-Speicherung",
      "48-hour support response time",
    ],
    mostPopular: false,
    continuous: true,
  },
  {
    name: "Mittelständer",
    id: SubscriptionPlan.ProLg,
    price: { month: "€954", year: "€11.448" },
    description: "Für Ihren Wachstum.",
    features: [
      "5 User",
      "30 Tage Testversion",
      "Täglich aktualisierte Normen",
      "Richtlinienrecherche",
    ],
    mostPopular: true,
    continuous: true,
  },
];

interface Props {
  currentPlan?: SubscriptionPlan;
  currentFrequency?: BillingFrequency;
}

export const PlanSelector = ({ currentPlan, currentFrequency }: Props) => {
  const { t } = useTranslation();
  const [frequency, setFrequency] = useState(frequencies[0]);
  const [paymentMethod, setPaymentMethod] = useState(paymentMethods[0]);
  const [invoiceModalPlan, setInvoiceModalPlan] = useState<SubscriptionPlan | null>(null);
  const { startCheckout, isLoading: isCheckoutLoading } = useStartCheckout();
  const { changeSubscription, isLoading: isChangeLoading } =
    useChangeSubscription();

  const hasActivePlan =
    currentPlan &&
    currentPlan !== SubscriptionPlan.Free;

  const handleSelect = (plan: SubscriptionPlan) => {
    if (plan === SubscriptionPlan.Free) return;

    if (hasActivePlan) {
      changeSubscription({ plan, frequency: frequency.value });
    } else if (paymentMethod.value === PaymentMethod.Invoice) {
      setInvoiceModalPlan(plan);
    } else {
      startCheckout({
        plan,
        frequency: frequency.value,
        paymentMethod: paymentMethod.value,
      });
    }
  };

  const getCtaLabel = (tierId: SubscriptionPlan) => {
    if (tierId === SubscriptionPlan.Free) return t("settings.billing.status_free");
    if (hasActivePlan && tierId === currentPlan) return t("settings.billing.status_active");
    if (hasActivePlan) return t("settings.billing.action_change");
    return t("settings.billing.action_upgrade");
  };

  const isCurrentPlan = (tierId: SubscriptionPlan) =>
    hasActivePlan && tierId === currentPlan;

  return (
    <div className="">
      <div className="mx-auto px-4 lg:px-4">
        <div className="mt-8 flex justify-center">
          <RadioGroup
            value={frequency}
            onChange={setFrequency}
            className="grid grid-cols-2 gap-x-1 rounded-full p-1 text-center text-xs font-semibold leading-5 ring-1 ring-inset ring-gray-200 bg-white"
          >
            <RadioGroup.Label className="sr-only">
              {t("settings.billing.frequency_label")}
            </RadioGroup.Label>
            {frequencies.map((option) => (
              <RadioGroup.Option
                key={option.value}
                value={option}
                className={({ checked }) =>
                  classNames(
                    checked ? "bg-cyan-600 text-white" : "text-gray-500",
                    "cursor-pointer rounded-full px-2.5 py-1"
                  )
                }
              >
                <span>{option.label}</span>
              </RadioGroup.Option>
            ))}
          </RadioGroup>
        </div>
        {!hasActivePlan && (
          <div className="mt-6 flex justify-center">
            <RadioGroup
              value={paymentMethod}
              onChange={setPaymentMethod}
              className="grid grid-cols-3 gap-x-1 rounded-full p-1 text-center text-xs font-semibold leading-5 ring-1 ring-inset ring-gray-200 bg-white"
            >
              <RadioGroup.Label className="sr-only">
                {t("settings.billing.pm_label")}
              </RadioGroup.Label>
              {paymentMethods.map((option) => (
                <RadioGroup.Option
                  key={option.value}
                  value={option}
                  className={({ checked }) =>
                    classNames(
                      checked ? "bg-cyan-600 text-white" : "text-gray-500",
                      "cursor-pointer rounded-full px-2.5 py-1"
                    )
                  }
                >
                  <span>{t(option.label)}</span>
                </RadioGroup.Option>
              ))}
            </RadioGroup>
          </div>
        )}
        <div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={classNames(
                isCurrentPlan(tier.id)
                  ? "ring-2 ring-cyan-600"
                  : "ring-1 ring-gray-200",
                "bg-white rounded-3xl p-8 xl:p-10"
              )}
            >
              <h3
                id={tier.id}
                className="text-lg font-semibold leading-8 text-gray-900"
              >
                {tier.name}
              </h3>
              <p className="mt-4 text-sm leading-6 text-gray-600">
                {tier.description}
              </p>
              <p className="mt-6 flex items-baseline gap-x-1">
                <span className="text-4xl font-bold tracking-tight text-gray-900">
                  {tier.price[frequency.value]}
                </span>
                <span className="text-sm font-semibold leading-6 text-gray-600">
                  {frequency.priceSuffix}
                </span>
              </p>
              <button
                onClick={() => handleSelect(tier.id)}
                disabled={
                  tier.id === SubscriptionPlan.Free ||
                  isCurrentPlan(tier.id) ||
                  isCheckoutLoading ||
                  isChangeLoading
                }
                aria-describedby={tier.id}
                className={classNames(
                  isCurrentPlan(tier.id)
                    ? "bg-gray-300 text-white cursor-default"
                    : tier.id === SubscriptionPlan.Free
                    ? "bg-gray-300 text-white cursor-default"
                    : "bg-cyan-600 text-white shadow-sm hover:bg-cyan-500 focus-visible:outline-cyan-600",
                  "mt-6 block w-full rounded-md py-2 px-3 text-center text-sm font-semibold leading-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50"
                )}
              >
                {isCheckoutLoading || isChangeLoading
                  ? "..."
                  : getCtaLabel(tier.id)}
              </button>
              <ul
                role="list"
                className="mt-8 space-y-3 text-sm leading-6 text-gray-600 xl:mt-10"
              >
                {tier.features.map((feature) => (
                  <li key={feature} className="flex gap-x-3">
                    <CheckIcon
                      className="h-6 w-5 flex-none text-cyan-600"
                      aria-hidden="true"
                    />
                    {feature}
                  </li>
                ))}
                {tier.continuous && (
                  <li className="flex gap-x-3">
                    <SparklesIcon
                      className="h-6 w-5 flex-none text-cyan-600"
                      aria-hidden="true"
                    />
                    Stetig neue Funktionen
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>
      {invoiceModalPlan && (
        <InvoiceCheckoutModal
          show={!!invoiceModalPlan}
          plan={invoiceModalPlan}
          frequency={frequency.value}
          onClose={() => setInvoiceModalPlan(null)}
        />
      )}
    </div>
  );
};
